'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import { useTranslation } from 'react-i18next';
import type { LearningChapterDetail } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

// ChapterTree — curriculum accordion.
//
// `<details>`-based so it works without JS. Each chapter is a separate
// <details>; multiple can be open at once (intentional — users want to
// scan multiple chapters side by side).
//
// `defaultOpen` controls whether chapters render open on first paint.
// Content-first uses true (curriculum is the page's whole point).
// Decision-first and Trust-first use false (curriculum is supporting
// info, kept compact).

interface ChapterTreeProps {
  chapters: LearningChapterDetail[];
  /** Pre-open all chapters on first paint. Default false. */
  defaultOpen?: boolean;
  /** Compact spacing variant — used by Decision-first. */
  compact?: boolean;
  /** Optional tenantSlug + courseId for lesson links (lets users jump
   *  straight into a preview lesson). When absent, lessons render as
   *  static rows. */
  tenantSlug?: string;
  courseId?: string;
}

export function ChapterTree({
  chapters,
  defaultOpen = false,
  compact = false,
  tenantSlug,
  courseId,
}: ChapterTreeProps) {
  const { t } = useTranslation('common');
  if (chapters.length === 0) return null;
  return (
    <div className={compact ? 'cd-chapters cd-chapters-compact' : 'cd-chapters'}>
      {chapters.map((ch, i) => {
        const lessons = ch.lessons ?? [];
        return (
          <Card asChild key={ch.id ?? i}>
            <details className="cd-chapter" open={defaultOpen}>
              <summary className="cd-chapter-summary">
                <span className="cd-chapter-summary-title">{ch.title || `Bölüm ${i + 1}`}</span>
                {lessons.length > 0 && (
                  <span className="cd-chapter-summary-count">{lessons.length} ders</span>
                )}
              </summary>
              {lessons.length > 0 && (
                <ul className="cd-chapter-lessons">
                  {lessons.map((l, lessonIdx) => {
                    // lesson.id is optional per schema. Need it both
                    // for the React key and the lesson link href. If
                    // absent we fall back to an index-based key for
                    // React and disable linking (can't deep-link to a
                    // lesson without an id).
                    const lessonKey = l.id ?? `lesson-${lessonIdx}`;
                    const canLink = tenantSlug && courseId && l.id && l.preview;
                    const linkHref = canLink
                      ? `/${tenantSlug}/learn/${courseId}/play/${l.id}`
                      : undefined;
                    const Row = (
                      <>
                        <span className="cd-lesson-icon" aria-hidden>
                          ▶
                        </span>
                        <span className="cd-lesson-title">{l.title}</span>
                        {l.preview && (
                          <span className="cd-lesson-preview">{t('label.preview')}</span>
                        )}
                        {l.duration && <span className="cd-lesson-duration">{l.duration}</span>}
                      </>
                    );
                    return (
                      <li key={lessonKey}>
                        {linkHref ? (
                          <a
                            href={linkHref}
                            className="cd-lesson"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {Row}
                          </a>
                        ) : (
                          <div className="cd-lesson">{Row}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </details>
          </Card>
        );
      })}
    </div>
  );
}
